import type { LucideIcon } from 'lucide-react'
import {
  CalendarClock,
  Car,
  Clock,
  Lock,
  Mic,
  Paperclip,
  Pencil,
  Sparkles,
  UserCog,
} from 'lucide-react'

/**
 * Release notes registry — drives the "What's new" pop-up on the
 * Dashboard. Add the newest release at the TOP of the array; the
 * pop-up always picks index 0.
 *
 * Seen-state is stored in localStorage (`autoflo:lastSeenRelease`).
 * A user dismissing release X never sees X again on that device; a
 * new release Y appears immediately on their next Dashboard visit.
 * No backend round-trip — keeps the announcement infra trivial.
 *
 * Entries should be small and concrete. We're talking to a mechanic in
 * a busy shop, not writing a marketing changelog — each item is one
 * action they can take, with a Lucide icon that mirrors the in-app
 * affordance (Mic / Paperclip / Pencil / Lock, etc.). Avoid jargon and
 * version-control speak.
 */
export type ReleaseItem = {
  icon: LucideIcon
  // i18n key under `whats_new.releases.<version>.items.<id>` — the
  // pop-up renders `t(\`${baseKey}.title\`)` and `t(\`${baseKey}.body\`)`.
  // Kept here as the *identifier* (not the displayed text) so locale
  // files own the wording.
  id: string
}

export type Release = {
  // Used as the localStorage seen-marker and as the i18n namespace
  // root: `whats_new.releases.${version}.*`. Date-style "YYYY-MM-DD"
  // sorts correctly as a string comparison if we ever need to.
  version: string
  // ISO date the release went out. Rendered with the locale-aware
  // `formatDate` helper in the dialog.
  date: string
  items: ReleaseItem[]
}

export const RELEASES: Release[] = [
  {
    version: '2026-05-20',
    date: '2026-05-20',
    items: [
      { id: 'eric_updates', icon: UserCog },
      { id: 'eric_permissions', icon: Lock },
      { id: 'voice_notes', icon: Mic },
      { id: 'voice_chat', icon: Sparkles },
      { id: 'attachments', icon: Paperclip },
      { id: 'live_appointments', icon: CalendarClock },
      { id: 'customer_notes', icon: Pencil },
      { id: 'calendar_hours', icon: Clock },
      { id: 'orphan_vehicles', icon: Car },
    ],
  },
]

export function latestRelease(): Release {
  return RELEASES[0]
}

const LAST_SEEN_KEY = 'autoflo:lastSeenRelease'

export function getLastSeenRelease(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(LAST_SEEN_KEY)
  } catch {
    // Private-browsing Safari throws on getItem. Treat as "never seen"
    // — the dialog re-renders, but that's gentler than crashing the
    // Dashboard for a corner case.
    return null
  }
}

export function setLastSeenRelease(version: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LAST_SEEN_KEY, version)
  } catch {
    // Same Safari ITP / private-mode edge as above — silently ignore.
  }
}
